import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const status = typeof raw.status === 'string' ? raw.status.toUpperCase() : 'PENDING';
  const completed = Boolean(raw.completed) || status === 'COMPLETE' || status === 'ERROR';
  const error = Boolean(raw.error) || status === 'ERROR';
  const progressValue = Number(raw.progress);
  const progress = Number.isFinite(progressValue)
    ? Math.max(0, Math.min(100, Math.round(progressValue)))
    : 0;

  return {
    ...raw,
    status,
    completed,
    error,
    progress,
    active: !completed && !error,
    message: typeof raw.message === 'string' ? raw.message : '',
  };
};

const splitStoriesText = (storiesText) => {
  if (typeof storiesText !== 'string' || storiesText.length === 0) {
    return [''];
  }
  const lines = storiesText
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return lines.length > 0 ? lines : [''];
};

const normalizeStoryLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    return [''];
  }
  return lines.map((line) => (line == null ? '' : String(line)));
};

const joinStoryLines = (lines) => {
  const normalized = normalizeStoryLines(lines);
  const compact = normalized
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (compact.length === 0) {
    return '';
  }
  return compact.join('\n');
};

const extractFileName = (headers, fallback) => {
  const disposition = headers?.['content-disposition'];
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!match) {
    return fallback;
  }
  const encoded = match[1] || match[2];
  try {
    return decodeURIComponent(encoded);
  } catch (error) {
    return encoded || fallback;
  }
};

const downloadBlob = (blobData, filename) => {
  const blob = blobData instanceof Blob
    ? blobData
    : new Blob([blobData ?? new ArrayBuffer(0)]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function StepTwoGenerationPanel({
  projectId,
  apiClient,
  refreshSignal = 0,
  disabled = false,
  disabledReason = null,
}) {
  const [userStoryJob, setUserStoryJob] = useState(null);
  const [userStoryArtifact, setUserStoryArtifact] = useState(null);
  const [userStoryLines, setUserStoryLines] = useState(['']);
  const [userStoryLoading, setUserStoryLoading] = useState(false);
  const [userStoryError, setUserStoryError] = useState(null);
  const [userStorySaving, setUserStorySaving] = useState(false);
  const [userStoryDownloading, setUserStoryDownloading] = useState(false);

  const hasUserStoryArtifact = Boolean(userStoryArtifact?.id);

  const loadUserStoryArtifactByJob = useCallback(async (jobId) => {
    if (!jobId) {
      return;
    }
    const response = await apiClient.get(`/api/projects/${projectId}/story-qa/user-stories/jobs/${jobId}/artifact`);
    const artifact = response?.data ?? null;
    setUserStoryArtifact(artifact);
    setUserStoryLines(splitStoriesText(artifact?.storiesText));
  }, [apiClient, projectId]);

  const pollUserStoryJob = useCallback(async (jobId) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/user-stories/jobs/${jobId}`);
      const normalized = normalizeJob(response?.data);
      if (!normalized) {
        return;
      }
      setUserStoryJob(normalized);
      if (normalized.completed && !normalized.error) {
        await loadUserStoryArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setUserStoryError(error?.response?.data?.error || error.message || 'Не удалось получить статус генерации user stories.');
    }
  }, [apiClient, loadUserStoryArtifactByJob, projectId]);

  const loadInitialState = useCallback(async () => {
    setUserStoryLoading(true);
    setUserStoryError(null);

    try {
      const [userArtifactRes, userActiveRes] = await Promise.allSettled([
        apiClient.get(`/api/projects/${projectId}/story-qa/user-stories/artifacts/latest`),
        apiClient.get(`/api/projects/${projectId}/story-qa/user-stories/jobs/active`),
      ]);

      if (userArtifactRes.status === 'fulfilled') {
        const artifact = userArtifactRes.value?.data ?? null;
        setUserStoryArtifact(artifact);
        setUserStoryLines(splitStoriesText(artifact?.storiesText));
      }

      if (userActiveRes.status === 'fulfilled') {
        setUserStoryJob(normalizeJob(userActiveRes.value?.data));
      }
    } catch (error) {
      setUserStoryError(error?.response?.data?.error || error.message || 'Не удалось загрузить данные шага 2.');
    } finally {
      setUserStoryLoading(false);
    }
  }, [apiClient, projectId]);

  useEffect(() => {
    setUserStoryJob(null);
    setUserStoryArtifact(null);
    setUserStoryLines(['']);
    loadInitialState();
  }, [loadInitialState, projectId]);

  useEffect(() => {
    if (refreshSignal > 0) loadInitialState();
  }, [loadInitialState, refreshSignal]);

  useEffect(() => {
    if (!userStoryJob?.active || !userStoryJob?.jobId) {
      return undefined;
    }

    const timer = setInterval(() => {
      pollUserStoryJob(userStoryJob.jobId);
    }, 2000);

    return () => clearInterval(timer);
  }, [pollUserStoryJob, userStoryJob?.active, userStoryJob?.jobId]);

  const handleGenerateUserStories = useCallback(async () => {
    if (disabled || userStoryJob?.active) {
      return;
    }

    setUserStoryError(null);
    try {
      const response = await apiClient.post(`/api/projects/${projectId}/story-qa/user-stories/generate`);
      const normalized = normalizeJob(response?.data);
      setUserStoryJob(normalized);
      if (normalized?.completed && !normalized.error) {
        await loadUserStoryArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setUserStoryError(error?.response?.data?.error || error.message || 'Не удалось запустить генерацию user stories.');
    }
  }, [apiClient, disabled, loadUserStoryArtifactByJob, projectId, userStoryJob?.active]);

  const handleAddStoryLine = useCallback(() => {
    setUserStoryLines((prev) => [...prev, '']);
  }, []);

  const handleRemoveStoryLine = useCallback((index) => {
    setUserStoryLines((prev) => {
      const next = prev.filter((_, lineIndex) => lineIndex !== index);
      return next.length > 0 ? next : [''];
    });
  }, []);

  const handleStoryLineChange = useCallback((index, value) => {
    setUserStoryLines((prev) => prev.map((line, lineIndex) => (
      lineIndex === index ? value : line
    )));
  }, []);

  const handleSaveUserStories = useCallback(async () => {
    if (!hasUserStoryArtifact || userStorySaving) {
      return;
    }

    setUserStorySaving(true);
    setUserStoryError(null);
    try {
      const response = await apiClient.put(
        `/api/projects/${projectId}/story-qa/user-stories/artifacts/${userStoryArtifact.id}`,
        { storiesText: joinStoryLines(userStoryLines) }
      );
      const artifact = response?.data ?? null;
      setUserStoryArtifact(artifact);
      setUserStoryLines(splitStoriesText(artifact?.storiesText));
    } catch (error) {
      setUserStoryError(error?.response?.data?.error || error.message || 'Не удалось сохранить user stories.');
    } finally {
      setUserStorySaving(false);
    }
  }, [apiClient, hasUserStoryArtifact, projectId, userStoryArtifact?.id, userStoryLines, userStorySaving]);

  const handleDownloadUserStories = useCallback(async () => {
    if (!hasUserStoryArtifact || userStoryDownloading) {
      return;
    }

    setUserStoryDownloading(true);
    setUserStoryError(null);
    try {
      const response = await apiClient.get(
        `/api/projects/${projectId}/story-qa/user-stories/artifacts/${userStoryArtifact.id}/download`,
        { responseType: 'blob' }
      );
      const filename = extractFileName(response?.headers, 'user-stories.docx');
      downloadBlob(response?.data, filename);
    } catch (error) {
      setUserStoryError(error?.response?.data?.error || error.message || 'Не удалось скачать user stories.');
    } finally {
      setUserStoryDownloading(false);
    }
  }, [apiClient, hasUserStoryArtifact, projectId, userStoryArtifact?.id, userStoryDownloading]);

  const userStoryStatus = useMemo(() => {
    if (userStoryJob?.active) {
      return { label: 'В процессе', color: 'info' };
    }
    if (userStoryJob?.error) {
      return { label: 'Ошибка', color: 'error' };
    }
    if (hasUserStoryArtifact) {
      return { label: 'Готово', color: 'success' };
    }
    return { label: 'Не запущено', color: 'default' };
  }, [hasUserStoryArtifact, userStoryJob?.active, userStoryJob?.error]);

  return (
    <Paper className="surface-glow" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h6">Шаг 2. User stories</Typography>
          <Chip size="small" label={`User stories: ${userStoryStatus.label}`} color={userStoryStatus.color} variant="outlined" />
        </Stack>

        {disabled && (
          <Alert severity="info">
            {disabledReason || 'LLM-операции временно недоступны.'}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">User stories для Dev</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleGenerateUserStories}
                disabled={disabled || userStoryJob?.active || userStoryLoading}
              >
                {userStoryJob?.active ? 'Генерация…' : 'Сгенерировать'}
              </Button>
            </Stack>

            {userStoryLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Загрузка user stories…</Typography>
              </Stack>
            ) : null}

            {userStoryError && <Alert severity="error">{userStoryError}</Alert>}

            {userStoryJob?.active && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {userStoryJob.message || 'Генерация user stories в процессе…'}
                </Typography>
                <LinearProgress variant="determinate" value={userStoryJob.progress || 0} />
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Построчное редактирование user stories.
              </Typography>
              <Button size="small" onClick={handleAddStoryLine}>Добавить строку</Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                {userStoryLines.map((line, index) => (
                  <Stack
                    key={`user-story-line-${index}`}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={`#${index + 1}`} />
                      <Tooltip title="Удалить строку">
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleRemoveStoryLine(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                    <TextField
                      value={line}
                      onChange={(event) => handleStoryLineChange(index, event.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Как <роль>, я хочу <действие>, чтобы <ценность>"
                    />
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveUserStories}
                disabled={!hasUserStoryArtifact || userStorySaving}
              >
                {userStorySaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleDownloadUserStories}
                disabled={!hasUserStoryArtifact || userStoryDownloading}
              >
                {userStoryDownloading ? 'Скачивание…' : 'Скачать DOCX'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
